import React, { useEffect, useState } from "react";
import { PhoneMissed, MessageSquare, Sparkles, Calendar, CheckCircle2 } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";

const AI_BG = "https://static.prod-images.emergentagent.com/jobs/d8e47eb4-9ceb-4272-ba98-a0a7b79b0bb8/images/07945e2fa795eceeba522308039b67ea407312fd3fd8b56c6da40870cdf3ddea.png";

export const AIDemoSection = () => {
  const { t } = useI18n();
  const [visible, setVisible] = useState([]);
  const msgs = t.plumberDemo.messages;

  useEffect(() => {
    setVisible([msgs[0]]);
    const id = setInterval(() => {
      setVisible((v) => {
        if (v.length >= msgs.length) {
          clearInterval(id);
          return v;
        }
        return [...v, msgs[v.length]];
      });
    }, 1800);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs]);

  const steps = [
    { label: t.demo.step1, icon: PhoneMissed, color: "red" },
    { label: t.demo.step2, icon: MessageSquare, color: "blue" },
    { label: t.demo.step3, icon: Sparkles, color: "purple" },
    { label: t.demo.step4, icon: Sparkles, color: "blue" },
    { label: t.demo.step5, icon: CheckCircle2, color: "cyan" },
  ];

  const activeStep = Math.min(steps.length - 1, Math.floor(visible.length / 2));

  return (
    <section className="relative py-20 lg:py-24 overflow-hidden" data-testid="ai-demo-section">
      <div className="absolute inset-0 -z-10 opacity-30">
        <img src={AI_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060B19] via-transparent to-[#060B19]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-12">
          <div className="eyebrow mb-3">{t.plumberDemo.eyebrow}</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">{t.plumberDemo.title}</h2>
          <p className="text-slate-400 mt-4 text-lg">{t.plumberDemo.sub}</p>
        </div>

        {/* Step timeline */}
        <div className="hidden md:flex items-center justify-between gap-2 mb-10">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const active = i <= activeStep;
            return (
              <React.Fragment key={s.label}>
                <div className={`flex flex-col items-center transition-all ${active ? "opacity-100" : "opacity-40"}`} data-testid={`demo-step-${i}`}>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center border-2 ${active ? "border-cyan-400 bg-cyan-400/10 text-cyan-300 glow-cyan" : "border-white/15 bg-white/5 text-slate-400"}`}>
                    <Icon size={20} />
                  </div>
                  <div className={`text-xs mt-2 font-medium ${active ? "text-white" : "text-slate-500"}`}>{s.label}</div>
                </div>
                {i < steps.length - 1 && (
                  <div className={`flex-1 h-px ${i < activeStep ? "bg-gradient-to-r from-cyan-400 to-blue-500" : "bg-white/10"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Chat */}
        <div className="glass-strong rounded-2xl p-5 sm:p-7 max-w-3xl mx-auto" data-testid="ai-demo-chat">
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold text-sm">PP</div>
              <div>
                <div className="text-white font-semibold text-sm">{t.plumberDemo.businessName}</div>
                <div className="text-xs text-slate-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse-dot" />{t.demo.aiStatus}</div>
              </div>
            </div>
            <div className="text-[10px] font-mono text-slate-500 tracking-widest">TP-AGENT</div>
          </div>

          <div className="space-y-3 min-h-[400px]">
            {visible.map((m, i) => {
              if (m.from === "system") {
                return (
                  <div key={m.text} className="flex items-center justify-center gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg py-2 px-3 mx-auto w-fit animate-fade-up">
                    <PhoneMissed size={14} />{m.text}
                  </div>
                );
              }
              const isAI = m.from === "ai";
              return (
                <div key={m.text} className={`flex ${isAI ? "justify-start" : "justify-end"} animate-fade-up`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${isAI ? "bg-blue-600 text-white rounded-bl-sm" : "bg-white/10 text-white rounded-br-sm border border-white/10"}`}>
                    <div className="text-sm leading-snug">{m.text}</div>
                    <div className={`text-[10px] mt-1 ${isAI ? "text-blue-100" : "text-slate-400"}`}>{m.t}</div>
                  </div>
                </div>
              );
            })}
            {visible.length < msgs.length && (
              <div className="flex justify-start">
                <div className="bg-blue-600/40 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-dot" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-dot" style={{ animationDelay: "0.2s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-dot" style={{ animationDelay: "0.4s" }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
